/* eslint-disable max-lines */
import { exploreModelManipulator } from '@logz-pkg/models';
import { cloneDeep, get, isNil } from 'lodash';
import { LOGZIO_HIGHLIGHT_TAG, MAX_AGG_SIZE } from './constants';
import { luceneDslUtils } from './utils';
const getDslFilters = (elasticsearchFilters, time, equal = true) => {
    const dslFilters = exploreModelManipulator.convertFiltersToElastic({ filters: elasticsearchFilters || [] });
    if (!dslFilters.bool.filter) {
        dslFilters.bool.filter = [];
    }
    if (time?.start || time?.end) {
        const rangeQuery = {
            '@timestamp': {
                format: 'strict_date_optional_time',
            },
        };
        if (time.start) {
            rangeQuery['@timestamp'][equal ? 'gte' : 'gt'] = time.start;
        }
        if (time.end) {
            rangeQuery['@timestamp'][equal ? 'lte' : 'lt'] = time.end;
        }
        dslFilters.bool.filter.push({
            range: rangeQuery,
        });
    }
    return dslFilters;
};
/**
 * OpenSearch's own default `shard_size` for a `terms` agg. Written explicitly on the terms levels the
 * bucket budget reasons about, so the candidate count it assumes is the one the cluster uses rather than
 * a default the cluster is free to change.
 *
 * Capped at `MAX_AGG_SIZE`, which the gateway applies to `shard_size` as well: the 1.5x + 10 default
 * lands at 1510 for a `size` of 1000 and the search is refused outright. `size` itself is already
 * clamped to the same cap, so the result never drops below it — a `shard_size` under `size` would be
 * raised back to `size` by OpenSearch anyway.
 */
export const resolveShardSize = (size) => Math.min(Math.ceil(size * 1.5) + 10, MAX_AGG_SIZE);
const getDateHistogramAggregation = ({ interval, shouldAddExtendedBounds, shouldFillEmptyBuckets = true, }) => ({
    field: '@timestamp',
    min_doc_count: shouldFillEmptyBuckets ? 0 : 1,
    calendar_interval: interval.interval,
    hard_bounds: {
        min: interval.upperBounds.start,
        max: interval.upperBounds.end,
    },
    // `extended_bounds` widens the filled grid, so it means nothing once the filling is off.
    ...(shouldAddExtendedBounds && shouldFillEmptyBuckets
        ? {
            extended_bounds: {
                min: interval.lowerBounds.start,
                max: interval.lowerBounds.end,
            },
        }
        : undefined),
});
const otherValuesPayloadBuilder = ({ payload, byTermAgg }) => {
    const { accounts, term, dslFilters, interval, shouldAddExtendedBounds, useExactMatch } = payload;
    const otherValuesPayload = {
        query: dslFilters,
        size: 0,
        source: { excludes: luceneDslUtils.getAccountsWithPrefix(accounts) },
        aggs: {
            'other-filter': {
                aggs: {
                    dateHistogram: {
                        // One filter bucket, so filling the grid costs one interval's worth of buckets, not one
                        // per candidate term. It also keeps "Other" on the same axis as the padded term series.
                        date_histogram: getDateHistogramAggregation({ interval, shouldAddExtendedBounds }),
                    },
                },
                filters: {
                    filters: {
                        termExist: {
                            bool: {
                                must: [],
                                filter: [{ exists: { field: term } }],
                                should: [],
                                must_not: [],
                            },
                        },
                    },
                },
            },
        },
    };
    byTermAgg.buckets.forEach(bucket => {
        if (bucket.key !== (payload.missingBucketLabel || 'Missing')) {
            const value = bucket.key_as_string ?? bucket.key;
            otherValuesPayload.aggs['other-filter'].filters.filters.termExist.bool.must_not.push(useExactMatch ? { term: { [term]: value } } : { match_phrase: { [term]: value } });
        }
    });
    return otherValuesPayload;
};
const searchWithDateHistogramPayload = ({ filters, interval, accounts, shouldAddExtendedBounds, }) => {
    return {
        trackTotalHits: false,
        query: filters,
        size: 0,
        source: { excludes: luceneDslUtils.getAccountsWithPrefix(accounts) },
        aggs: {
            agg: {
                date_histogram: getDateHistogramAggregation({ interval, shouldAddExtendedBounds }),
            },
        },
    };
};
const searchWithDateRangePayload = ({ filters, field, ranges, accounts, timeZone, }) => ({
    trackTotalHits: false,
    query: filters,
    size: 0,
    source: { excludes: luceneDslUtils.getAccountsWithPrefix(accounts) },
    aggs: {
        agg: {
            date_range: { field, ranges, ...(timeZone && { time_zone: timeZone }) },
        },
    },
});
// Numeric range bucket (OSD `range` agg). Unlike `date_range`, it must NOT carry a `time_zone`:
// numeric fields (e.g. float) reject custom time zones, and the bounds are plain numbers, not dates.
const searchWithRangePayload = ({ filters, field, ranges, accounts, }) => ({
    trackTotalHits: false,
    query: filters,
    size: 0,
    source: { excludes: luceneDslUtils.getAccountsWithPrefix(accounts) },
    aggs: {
        agg: {
            range: { field, ranges },
        },
    },
});
export const BUCKET_MATRIX_OUTER_AGG_KEY = 'byBucket';
export const BUCKET_MATRIX_INNER_AGG_KEY = 'byBucket2';
/** Agg keys of a field's extent, by its position in the `fields` list. */
export const boundsAggKeys = (index) => ({
    min: `bound${index}Min`,
    max: `bound${index}Max`,
});
/**
 * One or two nested field buckets, deliberately without a date histogram: the panel plots a field
 * against a field, so a time dimension would multiply every bucket by an interval nobody asked for.
 */
const searchWithBucketMatrixPayload = ({ filters, outer, inner, accounts, metricSubAggs, }) => {
    const metrics = metricSubAggs && Object.keys(metricSubAggs).length > 0 ? { ...metricSubAggs } : undefined;
    const innerLevel = inner
        ? { [BUCKET_MATRIX_INNER_AGG_KEY]: { ...inner, ...(metrics && { aggs: metrics }) } }
        : undefined;
    // The innermost level always carries the metrics, since that is where a value is read from. An outer
    // `terms` level needs its own copy as well: it can be ordered by a metric, and OpenSearch resolves
    // that order path against the level's OWN sub-aggregations, rejecting the search when it is missing.
    // A histogram level is always ordered by key, so it never needs one.
    const outerCarriesMetrics = Boolean(metrics) && (!inner || Boolean(outer.terms));
    const outerAggs = { ...innerLevel, ...(outerCarriesMetrics && metrics) };
    return {
        query: filters,
        size: 0,
        source: { excludes: luceneDslUtils.getAccountsWithPrefix(accounts) },
        aggs: {
            [BUCKET_MATRIX_OUTER_AGG_KEY]: {
                ...outer,
                ...(Object.keys(outerAggs).length > 0 && { aggs: outerAggs }),
            },
        },
    };
};
/**
 * The extent of each field, which an `auto` histogram interval is derived from. OSD runs the same
 * pre-flight: OpenSearch has no auto mode for a numeric histogram, so the interval is a client
 * decision and needs the data's own min and max.
 */
const searchWithBoundsPayload = ({ filters, fields, accounts }) => ({
    query: filters,
    size: 0,
    source: { excludes: luceneDslUtils.getAccountsWithPrefix(accounts) },
    aggs: Object.fromEntries(fields.flatMap((field, index) => {
        const { min, max } = boundsAggKeys(index);
        return [
            [min, { min: { field } }],
            [max, { max: { field } }],
        ];
    })),
});
// The `centroid` sub-aggregation is the average position of the documents inside the cell. Without it a
// marker can only be placed at the geometric centre of its geohash rectangle, which drifts noticeably
// from the data at coarse precisions.
const searchWithGeohashGridPayload = ({ filters, field, precision, size, accounts, }) => ({
    trackTotalHits: false,
    query: filters,
    size: 0,
    source: { excludes: luceneDslUtils.getAccountsWithPrefix(accounts) },
    aggs: {
        agg: {
            geohash_grid: { field, precision, size },
            aggs: {
                centroid: { geo_centroid: { field } },
            },
        },
    },
});
const searchWithMetricPayload = ({ filters, accounts, }) => ({
    trackTotalHits: false,
    query: filters,
    size: 0,
    source: { excludes: luceneDslUtils.getAccountsWithPrefix(accounts) },
    aggs: {},
});
const updateRange = (dsl, time) => {
    const copiedDsl = cloneDeep(dsl);
    copiedDsl.bool.filter?.forEach(f => {
        const range = f.range;
        if (!isNil(range)) {
            range['@timestamp'] = {
                ...range['@timestamp'],
                gte: time.start,
                lte: time.end,
            };
        }
    });
    return copiedDsl;
};
const termHistogramPayload = (payload) => {
    const { limit, accounts, term, dslFilters, interval, shouldAddExtendedBounds, showMissing, missingBucketLabel } = payload;
    return {
        query: dslFilters,
        size: 0,
        aggs: {
            byTerm: {
                terms: {
                    field: term,
                    ...((showMissing && { missing: missingBucketLabel || 'Missing' }) || {}),
                    order: {
                        _count: 'desc',
                    },
                    size: limit,
                },
                aggs: {
                    byDate: { date_histogram: getDateHistogramAggregation({ interval, shouldAddExtendedBounds }) },
                },
            },
        },
        source: { excludes: luceneDslUtils.getAccountsWithPrefix(accounts) },
    };
};
/** Agg key of a nested terms level by its position: what `buildNestedTermsAggs` writes and a reader walks. */
export const nestedTermsAggKey = (levelIndex) => levelIndex === 0 ? 'byTerm' : `byTerm${levelIndex + 1}`;
/**
 * Nests the levels innermost first. `firstLevelIndex` is the position of `terms[0]` in the whole
 * group-by, so a sub-tree built for the levels below an Other bucket keeps the keys the reader expects.
 */
const buildNestedTermsAggs = ({ terms, innerAggs, firstLevelIndex = 0, }) => terms.reduceRight((aggs, term, levelIndex) => ({
    [nestedTermsAggKey(firstLevelIndex + levelIndex)]: {
        terms: {
            field: term.field,
            ...(term.showMissing && { missing: term.missingBucketLabel || 'Missing' }),
            ...(term.include && { include: term.include }),
            ...(term.exclude && { exclude: term.exclude }),
            order: term.order ?? { _count: 'desc' },
            size: term.size,
            shard_size: resolveShardSize(term.size),
        },
        aggs,
    },
}), innerAggs);
/** Agg key of the keyed `filters` level an Other bucket request answers under. */
export const OTHER_BUCKET_AGG_KEY = 'other-filter';
const otherBucketFilterKey = (index) => `other-${index}`;
const readBucketKey = (bucket) => bucket.key_as_string ?? String(bucket.key);
const isMissingBucketKey = (term, key) => Boolean(term.showMissing) && key === (term.missingBucketLabel || 'Missing');
/**
 * Selects one bucket's value of a field, for a parent above the Other level and for each value the
 * Other level excludes alike. `match_phrase` only runs on text fields — on a number every shard throws
 * and OpenSearch masks it as a `reducePhase.aggregations is null` NPE — so a numeric key is matched
 * exactly whatever the caller asked.
 */
const buildValueClause = ({ field, bucket, useExactMatch, }) => useExactMatch || typeof bucket.key === 'number'
    ? { term: { [field]: bucket.key_as_string ?? bucket.key } }
    : { match_phrase: { [field]: readBucketKey(bucket) } };
/**
 * The Other bucket of one terms level, computed the way OSD computes it: one keyed filter per parent
 * bucket the main response returned, selecting that parent's documents whose value of the field is none
 * of the values drawn under it. The levels below the Other level are nested under the filters agg with
 * the keys the main payload gave them, so a reader walks both responses the same way.
 *
 * The keys are positional rather than OSD's hyphen-joined parent path, which it splits back on `-` and
 * so misroutes any value containing one; `entries` carries what each key stands for instead.
 *
 * No grid filling: with one filter per parent, `min_doc_count: 0` plus `extended_bounds` would repeat
 * the whole time axis under every parent (see `shouldFillEmptyBuckets`), so the caller pads it back.
 */
const otherBucketPayloadBuilder = ({ dslFilters, terms, otherLevelIndex, byTermAgg, interval, accounts, useExactMatch, }) => {
    const otherLevel = terms[otherLevelIndex];
    const filters = [];
    const buildFilter = (siblings, parentSteps) => {
        const drawn = siblings.filter(bucket => !isMissingBucketKey(otherLevel, readBucketKey(bucket)));
        const hasMissingBucket = drawn.length < siblings.length;
        const requiresField = !otherLevel.showMissing || hasMissingBucket;
        return {
            key: otherBucketFilterKey(filters.length),
            clause: {
                bool: {
                    must: [],
                    filter: [
                        ...parentSteps
                            .filter(step => !step.isMissing)
                            .map(step => buildValueClause({ field: step.field, bucket: step.bucket, useExactMatch })),
                        ...(requiresField ? [{ exists: { field: otherLevel.field } }] : []),
                    ],
                    should: [],
                    must_not: [
                        ...parentSteps.filter(step => step.isMissing).map(step => ({ exists: { field: step.field } })),
                        ...drawn.map(bucket => buildValueClause({ field: otherLevel.field, bucket, useExactMatch })),
                    ],
                },
            },
            entry: {
                parentPath: parentSteps.map(({ field, value, isMissing }) => ({ field, value, isMissing })),
                excludedValues: drawn.map(readBucketKey),
                requiresField,
            },
        };
    };
    const walk = (buckets, levelIndex, parentSteps) => {
        // A parent with nothing drawn under it has nothing to exclude, and an exists filter alone would
        // count documents no bucket ever held.
        if (buckets.length === 0)
            return;
        if (levelIndex === otherLevelIndex) {
            filters.push(buildFilter(buckets, parentSteps));
            return;
        }
        const level = terms[levelIndex];
        buckets.forEach(bucket => {
            const value = readBucketKey(bucket);
            const { [nestedTermsAggKey(levelIndex + 1)]: child } = bucket;
            walk(child?.buckets ?? [], levelIndex + 1, [
                ...parentSteps,
                { field: level.field, value, isMissing: isMissingBucketKey(level, value), bucket },
            ]);
        });
    };
    walk(byTermAgg.buckets, 0, []);
    const dateHistogramAgg = {
        byDate: {
            date_histogram: getDateHistogramAggregation({
                interval,
                shouldAddExtendedBounds: false,
                shouldFillEmptyBuckets: false,
            }),
        },
    };
    return {
        payload: {
            query: dslFilters,
            size: 0,
            source: { excludes: luceneDslUtils.getAccountsWithPrefix(accounts) },
            aggs: {
                [OTHER_BUCKET_AGG_KEY]: {
                    filters: { filters: Object.fromEntries(filters.map(({ key, clause }) => [key, clause])) },
                    aggs: buildNestedTermsAggs({
                        terms: terms.slice(otherLevelIndex + 1),
                        innerAggs: dateHistogramAgg,
                        firstLevelIndex: otherLevelIndex + 1,
                    }),
                },
            },
        },
        entries: Object.fromEntries(filters.map(({ key, entry }) => [key, entry])),
    };
};
/** Agg key of the `filters` bucket's level, the one a keyed per-filter response is read out of. */
export const FILTERS_AGG_KEY = 'byFilters';
/**
 * A keyed `filters` agg with a nested date histogram: one bucket per author-defined query, each carrying
 * its own time series.
 *
 * The grid stays filled here, unlike a `terms` level (see `shouldFillEmptyBuckets`). A keyed filters agg
 * produces exactly one bucket per entry and has no `shard_size` candidates, so filling costs
 * `entries.length` intervals rather than one grid per candidate term — the same reasoning that keeps the
 * one-bucket `other-filter` agg filled. It also means a filter matching nothing comes back as a real
 * flat-zero series, which is what OSD draws.
 */
const filtersHistogramPayload = ({ dslFilters, entries, interval, accounts, shouldAddExtendedBounds, }) => ({
    query: dslFilters,
    size: 0,
    source: { excludes: luceneDslUtils.getAccountsWithPrefix(accounts) },
    aggs: {
        [FILTERS_AGG_KEY]: {
            filters: { filters: Object.fromEntries(entries.map(({ key, clause }) => [key, clause])) },
            aggs: { byDate: { date_histogram: getDateHistogramAggregation({ interval, shouldAddExtendedBounds }) } },
        },
    },
});
const nestedTermHistogramPayload = (payload) => {
    const { terms, accounts, dslFilters, interval, shouldAddExtendedBounds, shouldFillEmptyBuckets } = payload;
    const dateHistogramAgg = {
        byDate: {
            date_histogram: getDateHistogramAggregation({ interval, shouldAddExtendedBounds, shouldFillEmptyBuckets }),
        },
    };
    return {
        query: dslFilters,
        size: 0,
        aggs: buildNestedTermsAggs({ terms, innerAggs: dateHistogramAgg }),
        source: { excludes: luceneDslUtils.getAccountsWithPrefix(accounts) },
    };
};
const handleSortWithSearchAfter = ({ dslFilters, fetchNextPage, allLogs, sort, }) => {
    let querySort = [];
    const lastLog = allLogs && allLogs[allLogs.length - 1];
    let searchAfter = lastLog?.sort;
    if (sort?.length) {
        const { timeStampField, secondField } = luceneDslUtils.extractFieldsFromSortArr(sort);
        [secondField, timeStampField].forEach(field => {
            if (!field)
                return;
            const order = field.startsWith('-') ? 'desc' : 'asc';
            const cleanedField = field.startsWith('-') ? field.slice(1) : field;
            querySort.push({
                [cleanedField]: { order },
            });
        });
        if (querySort?.length === 1 && secondField) {
            querySort.push({
                '@timestamp': { order: secondField === '-' ? 'desc' : 'asc' },
            });
        }
        const searchAfterHasNull = fetchNextPage &&
            (searchAfter?.some(searchAfterItem => isNil(searchAfterItem)) ||
                (searchAfter?.length === 1 && querySort.find(item => Object.keys(item)[0] === '@timestamp')));
        if (searchAfterHasNull && sort?.length) {
            const timeStampInd = querySort.findIndex(item => Object.keys(item)[0] === '@timestamp');
            searchAfter = searchAfter?.[timeStampInd] && [searchAfter?.[timeStampInd]];
            querySort = [querySort.find(item => Object.keys(item)[0] === '@timestamp')];
            if (secondField) {
                dslFilters.bool.must_not?.push({ exists: { field: secondField.slice(1) } });
            }
        }
    }
    return { searchAfter, querySort };
};
const getLogzioHighLight = query => {
    const clonedQuery = cloneDeep(query);
    let isQueryStringExists = false;
    const musts = get(clonedQuery, 'bool.must', []).map(must => {
        if (must.query_string) {
            isQueryStringExists = true;
            must.query_string.default_field = '*';
        }
        return must;
    });
    if (isQueryStringExists) {
        clonedQuery.bool.must = musts;
        return {
            highlight_query: clonedQuery,
        };
    }
    return {};
};
const buildLogsQuerySearchPayload = ({ fetchNextPage, dslFilters, highlightQueryContainsQuery, allLogs, sort, accounts, }) => {
    const pageSize = fetchNextPage ? 100 : 300;
    const { searchAfter, querySort } = handleSortWithSearchAfter({ dslFilters, fetchNextPage, allLogs, sort });
    return {
        size: pageSize,
        trackTotalHits: true,
        query: dslFilters,
        sort: querySort,
        source: { excludes: luceneDslUtils.getAccountsWithPrefix(accounts) },
        highlight: {
            fields: {
                '*': highlightQueryContainsQuery ? getLogzioHighLight(dslFilters) : {},
            },
            pre_tags: [LOGZIO_HIGHLIGHT_TAG],
            post_tags: [LOGZIO_HIGHLIGHT_TAG],
            fragment_size: 2147483647,
            number_of_fragments: 0,
        },
        ...(fetchNextPage && searchAfter && { searchAfter }),
    };
};
export const queryBuilder = {
    otherValuesPayloadBuilder,
    otherBucketPayloadBuilder,
    termHistogramPayload,
    nestedTermHistogramPayload,
    filtersHistogramPayload,
    searchWithDateHistogramPayload,
    searchWithDateRangePayload,
    searchWithRangePayload,
    searchWithBucketMatrixPayload,
    searchWithBoundsPayload,
    searchWithGeohashGridPayload,
    searchWithMetricPayload,
    getDslFilters,
    buildLogsQuerySearchPayload,
};
//# sourceMappingURL=query-builder.js.map